import React from 'react';
import THEME from '../utils/theme';

// ── React Icons imports ──
import { MdLanguage, MdCastForEducation } from 'react-icons/md';
import { FaTools, FaLaptopCode, FaHome, FaFileAlt, FaBriefcase } from 'react-icons/fa';

const features = [
  { icon: MdLanguage,      text: 'Japanese Language Training', color: THEME.red    },
  { icon: MdCastForEducation, text: 'Japan Culture Training',  color: '#E6A817'    },
  { icon: FaTools,         text: 'Real Skill Training',        color: THEME.green  },
  { icon: FaLaptopCode,    text: 'Interview Preparation',      color: '#5B8DEF'    },
  { icon: FaHome,          text: 'Residential Facility',       color: THEME.red    },
  { icon: FaFileAlt,       text: 'Legal Process Guideline',    color: '#E6A817'    },
  { icon: FaBriefcase,     text: 'Japan Placement Support',    color: THEME.green  },
];

export default function Hero() {
  return (
    <section id="hero" style={{
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${THEME.navy} 0%, #1A2D42 60%, #0D1B2A 100%)`,
      display: 'flex', alignItems: 'center',
      position: 'relative', overflow: 'hidden',
      padding: '120px clamp(20px,4vw,40px) 80px',
    }}>
      {/* Grid pattern */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.04, pointerEvents: 'none',
        backgroundImage: `
          repeating-linear-gradient(0deg,  white 0px, white 1px, transparent 1px, transparent 60px),
          repeating-linear-gradient(90deg, white 0px, white 1px, transparent 1px, transparent 60px)
        `,
      }} />

      {/* Japan circle glow */}
      <div style={{
        position: 'absolute', right: '-6%', top: '50%',
        transform: 'translateY(-50%)',
        width: 580, height: 580, borderRadius: '50%',
        background: `radial-gradient(circle, ${THEME.red}60 0%, ${THEME.red}18 50%, transparent 70%)`,
        animation: 'floatY 6s ease-in-out infinite',
        pointerEvents: 'none',
      }} />

      {/* Left green accent bar */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: THEME.green }} />

      <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 1, width: '100%' }}>
        <div className="sk-hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>

          {/* ── Left ── */}
          <div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: `${THEME.green}20`, border: `1px solid ${THEME.green}40`,
              color: '#6FD9BC', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
              textTransform: 'uppercase', padding: '6px 16px', borderRadius: 2, marginBottom: 28,
              fontFamily: "'Syne', sans-serif",
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#6FD9BC', display: 'inline-block' }} />
              From Bangladesh to Japan — Your Dream Career Starts Here
            </div>

            <h1 style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: 'clamp(2.3rem, 5vw, 4rem)',
              fontWeight: 800, lineHeight: 1.08, color: 'white', marginBottom: 24,
            }}>
              Build Your Career <br />
              in Japan with{' '}
              <span style={{ color: THEME.red, textShadow: `0 0 40px ${THEME.red}80` }}>
                the Right Skills
              </span>
            </h1>

            <p style={{
              fontFamily: "'Noto Serif', serif",
              fontSize: 17, lineHeight: 1.75,
              color: 'rgba(255,255,255,0.72)',
              marginBottom: 40, maxWidth: 480, fontStyle: 'italic',
            }}>
              We train you in language, trade, and professional skills — so you can work in Japan with confidence and dignity.
            </p>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <a href="#apply" style={{
                background: THEME.red, color: 'white', border: 'none',
                padding: '14px 32px', borderRadius: 4,
                fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 15,
                cursor: 'pointer', letterSpacing: '0.04em',
                textDecoration: 'none', display: 'inline-block',
                transition: 'background 0.2s, transform 0.15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = THEME.redDark; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = THEME.red; e.currentTarget.style.transform = 'translateY(0)'; }}>
                Apply Now →
              </a>
              <a href="#courses" style={{
                background: 'transparent', color: 'rgba(255,255,255,0.85)',
                border: '2px solid rgba(255,255,255,0.35)',
                padding: '13px 30px', borderRadius: 4,
                fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 15,
                textDecoration: 'none', display: 'inline-block', transition: 'all 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                View Courses
              </a>
            </div>

            {/* Stats */}
            <div style={{
              display: 'flex', gap: 40, marginTop: 56,
              paddingTop: 40, borderTop: '1px solid rgba(255,255,255,0.1)', flexWrap: 'wrap',
            }}>
              {[
                { num: '500+', label: 'Successful Graduates' },
                { num: '7+',   label: 'Trade Courses'        },
                { num: '100%', label: 'Certified Training'   },
              ].map(s => (
                <div key={s.label}>
                  <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '1.9rem', fontWeight: 800, color: THEME.red, lineHeight: 1 }}>
                    {s.num}
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right card ── */}
          <div className="sk-hero-card-wrapper" style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{
              background: 'rgba(255,255,255,0.06)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 16, padding: 36, width: '100%', maxWidth: 380,
              animation: 'floatY 5s ease-in-out infinite',
            }}>
              <div style={{ textAlign: 'center', marginBottom: 28 }}>
                <div style={{ fontSize: 64, marginBottom: 8 }}>🇯🇵</div>
                <div style={{ fontFamily: "'Syne',sans-serif", color: 'white', fontWeight: 700, fontSize: 18 }}>
                  Japan-Ready Training
                </div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginTop: 4 }}>
                  Professional Skill Development Center
                </div>
              </div>

              {/* ── Feature list with React Icons ── */}
              {features.map(f => {
                const Icon = f.icon; // ← component হিসেবে ব্যবহারের জন্য
                return (
                  <div key={f.text} style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '12px 16px', background: 'rgba(255,255,255,0.06)',
                    borderRadius: 8, marginBottom: 8, border: '1px solid rgba(255,255,255,0.06)',
                  }}>
                    {/* React Icon */}
                    <Icon
                      size={20}
                      color={f.color}
                      style={{ flexShrink: 0 }}
                    />
                    <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, flex: 1 }}>{f.text}</span>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: f.color, flexShrink: 0 }} />
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>

      <style>{`
        @keyframes floatY {
          0%, 100% { transform: translateY(-50%) translateY(0px); }
          50%       { transform: translateY(-50%) translateY(-14px); }
        }
        @media (max-width: 768px) {
          .sk-hero-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .sk-hero-card-wrapper { display: none !important; }
        }
      `}</style>
    </section>
  );
}